import './SprayChart.scss';

import { SprayDto, SprayPoint } from 'types/CodegenReportTypes';
import Legend from 'components/Legend/Legend';
import React, { useCallback } from 'react';

import { ReactComponent as BatterSprayChartGraphic } from 'svg/tileGraphics/BatterSprayChart.svg';
import { playResultToColor } from 'utilities/colorHelpers';
import { useChartDataDisplay } from 'hooks/helperHooks';
import { playResultLegendItems } from 'utilities/constants';

const chartViewboxHeight = 315;
const chartViewboxWidth = 316;
const batterPositionX = 0.5 * chartViewboxWidth;
const batterPositionY = chartViewboxHeight - 37;
const dataToSvgScaleFactorX = 0.46;
const dataToSvgScaleFactorY = 0.46;

function generateRandomPoints(options?: { xMin?: number; xMax?: number; yMin?: number; yMax?: number }): SprayPoint[] {
  const points: SprayPoint[] = [];
  const lerp = (start: number, end: number, t: number): number => start + (end - start) * t;

  const xMin = options?.xMin ?? -275;
  const xMax = options?.xMax ?? 275;
  const yMin = options?.yMin ?? 150;
  const yMax = options?.yMax ?? 500;

  for (let i = 0; i < 10; i++) {
    points.push({ x: lerp(xMin, xMax, Math.random()), y: lerp(yMin, yMax, Math.random()), distance: 0, bearing: 0 });
  }

  return points;
}

const SprayPointMarker: React.FC<{ point: SprayPoint; color: string; size: number }> = ({ point, color, size }) => {
  const positionX = batterPositionX + point.x * dataToSvgScaleFactorX;
  const positionY = batterPositionY - point.y * dataToSvgScaleFactorY;
  const offset = size / 2;

  return (
    <rect
      x={positionX - offset}
      y={positionY - offset}
      width={size}
      height={size}
      stroke={color}
      strokeWidth={1}
      fill={`${color}80`}
      rx={1}
    />
  );
};

type Props = {
  sprays: SprayDto[];
  isLoading: boolean;
};

const SprayChart = ({ sprays, isLoading }: Props) => {
  const markerSize = 10;

  const simulateChartData = useCallback((isLoading: boolean) => {
    return isLoading
      ? [
          {
            playResult: 'Single',
            sprayPoints: generateRandomPoints({ yMax: 250 }),
          },
          {
            playResult: 'Double',
            sprayPoints: generateRandomPoints({ yMax: 300 }),
          },
          {
            playResult: 'Triple',
            sprayPoints: generateRandomPoints({ yMin: 300, yMax: 400 }),
          },
          {
            playResult: 'HomeRun',
            sprayPoints: generateRandomPoints({ yMin: 400, yMax: 500 }),
          },
          {
            playResult: 'Out',
            sprayPoints: generateRandomPoints({ yMax: 350 }),
          },
        ]
      : [];
  }, []);

  const chartDataDisplay = useChartDataDisplay(sprays, simulateChartData, isLoading);

  const filteredLegendItems = playResultLegendItems.filter((item) => chartDataDisplay.some((spray) => spray.playResult === item.label));

  return (
    <div className='SprayChart-container'>
      <div className='SprayChart-chart-wrapper'>
        <BatterSprayChartGraphic className='SprayChart-chart' />
        <svg className={`SprayChart-markers ${isLoading ? 'loading' : ''}`} viewBox={`0 0 ${chartViewboxWidth} ${chartViewboxHeight}`}>
          {chartDataDisplay.map((sprayData, index) => (
            <React.Fragment key={index}>
              {sprayData.sprayPoints.map((point, pointIndex) => (
                <SprayPointMarker
                  key={`${index}-${pointIndex}`}
                  point={point}
                  color={playResultToColor[sprayData.playResult]}
                  size={markerSize}
                />
              ))}
            </React.Fragment>
          ))}
        </svg>
      </div>
      <Legend items={filteredLegendItems} disableSorting={true} />
    </div>
  );
};

export default SprayChart;
